import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useQueryClient, useMutation } from 'react-query';

import { getRandomColor } from 'src/UIComponents/utils/colors';
import { Input, Button, Tag } from 'src/UIComponents';
import { createTag as createTagService, getTags } from 'src/api-services';

const Results = styled.div``;

const FormContainer = styled.div`
  max-width: 280px;
  display: flex;
  flex-direction: row;
`;

function TagRelate({ onSelected, exclude, ...rest }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isValidTerm, setValidTerm] = useState(false);
  const [isCreateButtonDisabled, setDisableCreateButton] = useState(false);
  const [tagSuggestions, setSearchResult] = useState([]);
  const searchInput = useRef();
  const queryClient = useQueryClient();

  const { mutate: createTag, isLoading: creatingTag } = useMutation(
    createTagService,
    {
      onSuccess: ({ data: newTag }) => {
        queryClient.setQueryData('tags', (cachedQuery = {}) => {
          const existingTags = cachedQuery.data || [];
          return {
            ...cachedQuery,
            data: [newTag, ...existingTags],
          };
        });
        setSearchTerm('');
        onSelected(newTag);
      },
    }
  );

  // Keep track if we have a valid term
  useEffect(() => {
    setValidTerm(searchTerm.length >= 2);
  }, [searchTerm]);

  // Do a new search if term changes
  useEffect(() => {
    if (!isValidTerm) {
      setSearchResult([]);
      return;
    }
    const trimmedTerm = searchTerm.trim();
    const timeoutId = setTimeout(() => {
      queryClient.fetchQuery(['tags', 'search', trimmedTerm], () =>
        getTags({
          q: trimmedTerm,
        }).then(({ data }) => setSearchResult(data))
      );
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [queryClient, searchTerm, isValidTerm]);

  useEffect(() => {
    // Disable button if it already exists
    const lookupTag = tagSuggestions.find(
      (tag) => tag.title.toLowerCase() === searchTerm.toLowerCase()
    );
    setDisableCreateButton(lookupTag);
  }, [searchTerm, tagSuggestions]);

  const saveAsNewTag = useCallback(
    (tagTitle) => {
      if (creatingTag || isCreateButtonDisabled || !isValidTerm) {
        return;
      }
      createTag({
        title: tagTitle.trim(),
        color: getRandomColor(),
      });
      setSearchTerm('');
    },
    [createTag, creatingTag, isCreateButtonDisabled, isValidTerm]
  );

  const handleOnChange = useCallback(
    (event) => {
      setSearchTerm(event.target.value);
    },
    [setSearchTerm]
  );

  const handleOnCreate = useCallback(() => {
    saveAsNewTag(searchTerm);
  }, [saveAsNewTag, searchTerm]);

  const handleOnSuggestion = useCallback(
    (suggestion) => {
      onSelected(suggestion);
      setSearchTerm('');
      searchInput.current.focus();
    },
    [onSelected]
  );

  const handleKeyEvent = useCallback(
    (event) => {
      const char = event.which || event.keyCode;
      switch (char) {
        case 13: // Enter
          if (tagSuggestions.length > 0) {
            onSelected(tagSuggestions[0]);
            setSearchTerm('');
          } else {
            saveAsNewTag(searchTerm);
          }
          searchInput.current.focus();
          break;
        case 9: // Tab
          break;
        case 27: // Escape
          setSearchTerm('');
          event.target.blur();
          break;
        default:
          break;
      }
    },
    [saveAsNewTag, searchTerm, tagSuggestions, onSelected]
  );

  return (
    <div {...rest}>
      <FormContainer>
        <Input
          ref={searchInput}
          placeholder="Search/create tags..."
          size="small"
          value={searchTerm}
          onChange={handleOnChange}
          onKeyDown={handleKeyEvent}
        />
        <Button
          size="small"
          $color="success"
          disabled={creatingTag || isCreateButtonDisabled}
          style={{
            display: isValidTerm ? 'block' : 'none',
          }}
          onClick={handleOnCreate}
        >
          Create
        </Button>
      </FormContainer>
      <Results className="mt-1">
        {tagSuggestions
          .filter((tag) => !exclude.includes(tag.id))
          .map((suggestion, index) => (
            <Tag
              key={suggestion.id}
              size="small"
              color={suggestion.color}
              className="mr-1"
              onClick={() => handleOnSuggestion(suggestion)}
            >
              {suggestion.title}
            </Tag>
          ))}
      </Results>
    </div>
  );
}

TagRelate.defaultProps = {
  exclude: [],
};

TagRelate.propTypes = {
  onSelected: PropTypes.func.isRequired,
  exclude: PropTypes.array,
};

export default TagRelate;
